// Entry point: which game is running (a local one or an online room), which screen is showing,
// how game events become toasts, banners and effects for this seat, and the frame loop.
import { G, setState, PCOL, NAMES, MODS, MOD_MAX, LEVELS, MAX_PLAYERS, genMap, shapeOf, defaultName, rushHour, maxCrowd } from '../sim/game.js';
import { UI, me } from './ui-state.js';
import { LocalGame, OnlineGame, LobbyBrowser } from './net.js';
import { MAPS } from '../sim/maps.js';
import { startTutorial, stopTutorial, skipStep, tutorialTick, tutorialOn, TUT_SEED, TUT_HOME } from './tutorial.js';
import { render, resize, readTheme, resetCam, zoomAt, shapeSVG } from './render.js';
import { paintThumb, dressCartouche, seaForSeed } from './mapart.js';
import { hud, toast, celebrate, $, resetHud, fmtTime } from './hud.js';
import { Music, SCORES } from './audio.js';
import './input.js';   // registers mouse, touch and key handlers

const VERSION = typeof __VERSION__ === 'string' ? __VERSION__ : 'dev';   // set from package.json at build time
const params = new URLSearchParams(location.search);
const DEV = params.has('dev');
const rnd = () => (Math.random() * 1e9) | 0;

let lastMapName = null;   // the map whose song is playing, so a new map (not an annex redraw) changes it
let net = null, screen = 'title', showHowto = false, nPlayersPref = 4, soloMods = [], soloLevel = 'normal';
export const NET = () => net;
export const getScreen = () => screen;

function useNet(n) {
  if (net) net.close();
  net = n;
  Object.assign(UI, { fx: [], pops: [], selLine: null, placing: null, drag: null, bend: null, unhook: null, carry: null, hoverMap: null, hoverChip: null, cam: null, rushWarned: false, watching: false, hover: null });
  resetHud();
}

// ---------- modes ----------
// the front page has no game behind it, just the tram scene the renderer draws for the title
function startDemo() {
  stopTutorial();
  UI.mode = 'title'; UI.demo = true; UI.seat = 0;
  useNet(null); setState(null);
  setScreen('title');
}
// solo opens on a named map that holds this many cities; Random map asks for a fresh one
const catalogueSeed = n => { const fits = MAPS.filter(m => m.max >= n); return fits.length ? fits[Math.floor(Math.random() * fits.length)].seed : rnd(); };
function newSolo(seed = catalogueSeed(nPlayersPref)) {
  UI.mode = 'solo'; UI.demo = false; UI.seat = 0;
  useNet(new LocalGame({ seed, np: nPlayersPref, cheats: DEV, mods: soloMods, level: soloLevel }));
  setScreen('draft');
}
function restartSolo() {
  const seed = net.seed, claim = net.state.seats[0].claim;
  newSolo(seed);
  if (claim != null) { net.send({ type: 'claim', rid: claim }); net.start(); }
}
const handlers = {
  joined(m) {
    UI.seat = m.seat; $('onlineErr').textContent = '';
    if (params.has('room')) { params.delete('room'); history.replaceState(null, '', location.pathname + (params.toString() ? '?' + params : '')); }
    if (screen === 'title') setScreen(m.rejoined && net.lobby && net.lobby.phase !== 'draft' ? 'play' : 'draft');
  },
  lobby(m) { if (screen === 'draft') refreshDraft(); if (m.phase !== 'draft' && screen === 'draft') setScreen('play'); },
  error(msg) {
    if (screen === 'title') { $('onlineErr').textContent = msg; if (!net || !net.code) startDemo(); }
    else if (!net || !net.code) { toast(msg, 4000); startDemo(); }
    else toast(msg, 4000);
  },
  status(s) { $('netPill').classList.toggle('hidden', s !== 'reconnecting'); },
};
function ensureOnline() {
  if (net && net.kind === 'online') return;
  UI.mode = 'online'; UI.demo = false;
  useNet(new OnlineGame(handlers));
}
const playerName = () => { const n = $('nameIn').value.trim(); try { localStorage.setItem('trammed-name', n); } catch { /* storage blocked */ } return n || 'Player'; };

// ---------- screens ----------
export function setScreen(s) {
  screen = s;
  if (s === 'title' || s === 'draft') { Music.scene('lobby'); lastMapName = null; }
  const hudOn = s === 'play' || s === 'pause';
  $('draft').classList.toggle('hidden', s !== 'draft');
  $('hub').classList.toggle('hidden', s !== 'title');
  browse(s === 'title');
  for (const id of ['top', 'panel', 'players', 'lines']) $(id).classList.toggle('hidden', !hudOn);
  $('cartouche').classList.toggle('hidden', !hudOn || !(G && G.map && G.map.name));
  if (G && G.map && G.map.name) { $('cartoucheName').textContent = G.map.name; dressCartouche($('cartouche'), G.map); }
  $('cam').classList.toggle('hidden', !(hudOn || s === 'draft'));
  $('cheats').classList.toggle('hidden', !hudOn || !(G && G.cheats));
  $('hint').classList.toggle('hidden', !hudOn);
  $('end').style.display = 'none';
  $('menu').classList.toggle('hidden', s !== 'pause');
  $('menu').classList.toggle('pause', s === 'pause');
  if (net && net.kind === 'local' && s === 'pause') net.paused = true;
  if (net && net.kind === 'local' && s === 'play') net.paused = false;
  showHowto = false;
  resetHud(); refreshMenu(); refreshDraft();
  resize();
}
function refreshMenu() {
  const btn = (a, label, primary) => `<button data-a="${a}" class="${primary ? 'primary' : ''}">${label}</button>`;
  const music = btn('music', Music.on ? 'Music: on' : 'Music: off'), how = btn('howto', showHowto ? 'Hide how to play' : 'How to play');
  let h;
  $('hubMusic').textContent = Music.on ? 'Music: on' : 'Music: off';
  for (const id of ['hubCB', 'menuCB']) $(id).textContent = UI.colourBlind ? 'Colour-blind: on' : 'Colour-blind: off';
  $('hubHowBtn').textContent = showHowto ? 'Hide how to play' : 'How to play';
  // one set of rules, shown on the front page or in the pause menu
  (screen === 'title' ? $('hubHow') : $('menuBtns').parentNode).insertBefore($('howto'), screen === 'title' ? null : $('menu').querySelector('.vol'));
  if (screen === 'title') h = '';
  else if (net && net.kind === 'online') {
    $('menuTag').textContent = `Room ${net.code || ''} · the match keeps running`;
    h = btn('resume', 'Back to the game', true) + how + music + btn('title', 'Leave match');
  } else {
    $('menuTag').textContent = `Paused at ${fmtTime(G ? G.time : 0)}`;
    h = btn('resume', 'Resume', true) + btn('restart', 'Restart match') + btn('newmap', 'New map') + how + music + btn('title', 'Quit to title');
  }
  $('menuBtns').innerHTML = h;
  $('howto').classList.toggle('hidden', !showHowto);
  $('vol').value = Math.round(Music.volume * 100);
  for (const id of ['scoreSel', 'hubScore']) $(id).value = Music.score;
}
const esc = s => String(s).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
function refreshDraft() {
  $('draft').classList.toggle('tut', tutorialOn());   // the tutorial's match is fixed: no settings to change
  if (screen !== 'draft' || !net) return;
  const V = net.view, online = net.kind === 'online', host = net.isHost, claim = V && V.seats ? V.seats[UI.seat]?.claim : null;
  let h = '';
  if (!online || host) {
    const n = online ? (net.lobby ? net.lobby.np : 4) : nPlayersPref, humans = online && net.lobby ? net.lobby.seats.filter(s => s.human).length : 1;
    h = `<span>Cities</span><button data-a="pcount" data-v="${n - 1}" ${n - 1 < Math.max(2, humans) ? 'disabled' : ''} aria-label="Fewer">−</button><b class="count">${n}</b><button data-a="pcount" data-v="${n + 1}" ${n + 1 > MAX_PLAYERS ? 'disabled' : ''} aria-label="More">+</button>`;
    for (const k of [4, 8, 16, 30]) h += `<button data-a="pcount" data-v="${k}" class="${n === k ? 'on' : ''}" ${k < humans ? 'disabled' : ''}>${k}</button>`;
    h += `<button data-a="newmap" style="margin-left:auto" data-tip="New map|A fresh random map, not from the catalogue.">Random map</button>`;
  }
  const lob = online ? net.lobby : null, pub = !!(lob && lob.public);
  $('pcount').innerHTML = pub ? '' : h;
  // modifiers: chosen by the solo player or the host, shown to everyone else
  const active = online ? (lob ? lob.mods || [] : []) : soloMods, mine = !online || (host && !pub);
  $('modRow').innerHTML = (mine || active.length ? '<span>Modifiers</span>' : '') + Object.entries(MODS)
    .filter(([id]) => mine || active.includes(id))
    .map(([id, m]) => `<button data-a="mod" data-v="${id}" class="${active.includes(id) ? 'on' : ''}" data-tip="${esc(m.name)}|${esc(m.desc)}" ${mine && (active.includes(id) || active.length < MOD_MAX) ? '' : 'disabled'}>${esc(m.name)}</button>`).join('');
  // bot difficulty, the same way
  const lvl = online ? (lob && lob.level) || 'normal' : soloLevel;
  $('levelRow').innerHTML = '<span>Bots</span>' + Object.entries(LEVELS).filter(([id]) => mine || id === lvl)
    .map(([id, L]) => `<button data-a="level" data-v="${id}" class="${id === lvl ? 'on' : ''}" data-tip="${esc(L.name)} bots|${esc(L.desc)}" ${mine ? '' : 'disabled'}>${esc(L.name)}</button>`).join('');
  // the map: a thumbnail and its name, and a way to choose another for the solo player or host
  const seed = currentSeed(), n2 = currentCount(), cat = MAPS.find(m => m.seed === seed), mini = seed ? thumb(seed, n2) : '';
  $('mapRow').innerHTML = `<span>Map</span>${mini ? `<img class="mini" src="${mini}" alt="">` : '<i class="mini"></i>'}<span class="mapName"><b>${esc((V && V.map && V.map.name) || '')}</b><em>${cat ? `${formWord[cat.form]} · up to ${cat.max} cities` : 'a fresh map'}</em></span>` + (mine ? '<button data-a="maps" style="margin-left:auto">Choose map</button>' : '');
  $('draftPub').classList.toggle('hidden', !pub);
  if (pub) $('draftPub').innerHTML = `<b>Quick play · ${esc(lob.title)}</b> · <span class="leaves" id="leaves"></span>`;
  $('lobbyInfo').classList.toggle('hidden', !online);
  if (online && net.lobby) {
    $('roomCode').textContent = net.lobby.code;
    $('seatList').innerHTML = net.lobby.seats.map((s, i) => `<li>${shapeSVG(shapeOf(i), PCOL[i], 14)}<span class="${s.human && !s.connected ? 'off' : ''}">${s.human ? esc(s.name) : 'Bot'}${i === UI.seat ? ' (you)' : ''}${i === net.lobby.hostSeat ? ' · host' : ''}${s.human && !s.connected ? ' · away' : ''}</span></li>`).join('');
  }
  if (V && V.map && V.map.name) $('draftTitle').textContent = V.map.name;
  const canBegin = online ? host : claim != null;
  $('beginBtn').disabled = !canBegin;
  $('beginBtn').classList.toggle('hidden', online && (!host || pub));
  $('draftMsg').textContent = claim == null ? 'Click a region on the map to claim it.' : pub ? 'Good spot. The match leaves when the clock runs out.' : online && !host ? 'Good spot. Waiting for the host to start.' : 'Good spot. Rivals pick after you.';
}
function showEnd(title, text, canWatch) {
  $('endTitle').textContent = title; $('endText').textContent = text;
  $('watchBtn').classList.toggle('hidden', !canWatch);
  const online = net && net.kind === 'online';
  // online, Play again never comes back round for everyone, so a finished or fallen player heads to the main menu
  $('restartBtn').classList.toggle('hidden', online);
  $('endMenuBtn').classList.toggle('primary', online);
  $('newmapBtn').classList.toggle('hidden', online);
  $('end').style.display = 'flex';
}

// ---------- events from the game, told from this seat's point of view ----------
const who = id => (G.players[id] && G.players[id].name) || defaultName(id);
const city = who;
function handleEvent(ev) {
  const mine = ev.to === UI.seat;
  switch (ev.type) {
    case 'pop': if (mine && !UI.demo) UI.pops.push({ x: ev.x, y: ev.y, text: ev.text, t: 0 }); break;
    case 'notice': if (mine) toast(ev.text); break;
    case 'linked': if (mine) UI.selLine = ev.line; break;
    case 'start': if (me()) toast(`Drag from your ${NAMES[me().shape].toLowerCase()} factory to a gate to lay your first line.`, 5000); break;
    case 'overrun': {
      UI.fx.push({ x: ev.x, y: ev.y, t: 0, color: PCOL[ev.killer != null ? ev.killer : ev.victim] });
      if (UI.demo) break;
      if (ev.killer === UI.seat) celebrate(G.players[ev.victim], ev.bounty);
      else toast(ev.killer != null ? `${city(ev.victim)} was overrun. ${who(ev.killer)} claimed a ${ev.bounty} coin bounty.` : `${city(ev.victim)} was overrun.`, 4500);
      if (ev.victim === UI.seat && !UI.watching) {
        const alive = G.players.filter(q => q.alive).length;
        if (alive > 1) showEnd('Overrun', `You lasted ${fmtTime(G.time)}. When you fell, ${alive} cities were still running.`, true);
      }
      break;
    }
    case 'annex': if (!UI.demo) toast(`${who(ev.by)} annexed the ruins: +${ev.salvage} salvage and ${ev.hearts > 1 ? ev.hearts + ' new factories' : 'a new factory'}.`, 3500); break;
    case 'surge':
      if (UI.demo) break;
      if (ev.from === UI.seat) toast(`Surge loaded: ${ev.n} for ${who(ev.to)}. Get them moving before the grace runs out.`, 3500);
      else if (ev.to === UI.seat) toast(`${who(ev.from)} launched a surge of ${ev.n} at you!`, 3500);
      break;
    case 'end': {
      if (UI.demo) { const n = net; setTimeout(() => { if (net === n) startDemo(); }, 2500); break; }
      const w = ev.winner, p = me();
      if (w === UI.seat) showEnd('Last city running', `You held out for ${fmtTime(G.time)}. Processed ${p.stats.processed}, delivered ${p.stats.delivered}, passed through ${p.stats.passed}.`, false);
      else showEnd(w != null ? `${who(w)} wins` : 'Nobody wins', `The match lasted ${fmtTime(G.time)}.`, false);
      break;
    }
  }
}

// ---------- buttons that are about screens and rooms rather than the match ----------
export function actions(a, v) {
  switch (a) {
    case 'playSolo': playerName(); newSolo(); return true;
    case 'createRoom': ensureOnline(); net.create(playerName(), 4, []); return true;
    case 'tutorial': {
      // a three-city solo match on Great Britain with your city in the middle, then the missions
      playerName(); stopTutorial(); nPlayersPref = 3; soloMods = [];
      const keep = soloLevel; soloLevel = 'easy'; newSolo(TUT_SEED); soloLevel = keep;   // the tutorial's bots are gentle
      net.send({ type: 'claim', rid: TUT_HOME }); setState(net.view); refreshDraft();
      startTutorial(); return true;
    }
    case 'tutSkip': skipStep(); return true;
    case 'tutQuit': stopTutorial(); return true;
    case 'maps': openMaps(); return true;
    case 'mapsClose': $('maps').classList.add('hidden'); return true;
    case 'mapCount': picker.count = Math.max(2, Math.min(MAX_PLAYERS, +v)); renderMaps(); return true;
    case 'mapType': picker.type = v; renderMaps(); return true;
    case 'pickMap': pickMap(+v); return true;
    case 'quickJoin': $('onlineErr').textContent = ''; ensureOnline(); net.join(v, playerName()); return true;
    case 'level': {
      if (!LEVELS[v]) return true;
      if (net.kind === 'online') net.host('settings', { level: v }); else { soloLevel = v; newSolo(net.seed); }
      return true;
    }
    case 'mod': {
      const cur = net.kind === 'online' ? (net.lobby?.mods || []) : soloMods;
      const next = cur.includes(v) ? cur.filter(x => x !== v) : [...cur, v].slice(0, MOD_MAX);
      if (net.kind === 'online') net.host('settings', { mods: next }); else { soloMods = next; newSolo(net.seed); }
      return true;
    }
    case 'joinRoom': { const code = $('codeIn').value.trim(); if (code.length !== 4) { $('onlineErr').textContent = 'Room codes are 4 letters.'; return true; } ensureOnline(); net.join(code, playerName()); return true; }
    case 'copyLink': { const link = `${location.origin}${location.pathname}?room=${net.code}`; navigator.clipboard?.writeText(link).then(() => toast('Invite link copied.'), () => toast(link, 6000)); return true; }
    case 'pcount':
      if (net.kind === 'online') net.host('settings', { players: +v });
      else { nPlayersPref = +v; newSolo(net.seed); }
      return true;
    case 'newmap':
      if (net.kind === 'online') { if (screen === 'draft') net.host('reroll'); }
      else newSolo(rnd());
      return true;
    case 'begin': if (net.kind === 'online') net.start(); else if (net.state.seats[0].claim != null) { net.start(); setState(net.view); setScreen('play'); } return true;
    case 'refreshDraft': setState(net.view); refreshDraft(); return true;
    case 'restart': if (net.kind === 'online') net.host('rematch'); else restartSolo(); return true;
    case 'resume': setScreen('play'); return true;
    case 'menu': setScreen('pause'); return true;
    case 'title': if (net && net.kind === 'online') net.leave(); startDemo(); return true;
    case 'watch': UI.watching = true; $('end').style.display = 'none'; return true;
    case 'howto': showHowto = !showHowto; refreshMenu(); return true;
    case 'music': Music.toggle(); refreshMenu(); return true;
    case 'colourBlind': UI.colourBlind = !UI.colourBlind; try { localStorage.setItem('trammed-cb', UI.colourBlind ? '1' : '0'); } catch (e) { /* storage blocked */ } refreshMenu(); hud(); return true;
    case 'refreshMenu': refreshMenu(); return true;
    case 'pause': if (net.kind === 'local') net.paused = !net.paused; return true;
    case 'speed': if (net.kind === 'local') net.speed = net.speed === 1 ? 2 : net.speed === 2 ? 3 : 1; return true;
    case 'zoomIn': zoomAt(innerWidth / 2, innerHeight / 2, 1.25); return true;
    case 'zoomOut': zoomAt(innerWidth / 2, innerHeight / 2, 0.8); return true;
    case 'fit': resetCam(); return true;
    case 'panelToggle': UI.panelOpen = !UI.panelOpen; return true;
    case 'reload': location.reload(); return true;
    case 'escape':
      if (screen === 'play' && $('end').style.display !== 'flex') setScreen('pause');
      else if (screen === 'pause') setScreen('play');
      else if (screen === 'draft') { if (net && net.kind === 'online') net.leave(); startDemo(); }
      return true;
  }
  return false;
}

// ---------- quick play on the front page ----------
let browser = null, lobbies = [];
const clock = ms => { const t = Math.max(0, Math.ceil(ms / 1000)); return `${Math.floor(t / 60)}:${String(t % 60).padStart(2, '0')}`; };
function browse(on) {
  if (on && !browser) browser = new LobbyBrowser(list => { lobbies = list; renderLobbies(); }, () => { lobbies = null; renderLobbies(); });
  if (!on && browser) { browser.close(); browser = null; }
}
// a small picture of each lobby's island, drawn once per map
const thumbs = new Map(), thumbQueue = [];
let thumbBusy = false;
function thumb(seed, np) {
  const key = seed + ':' + np;
  if (thumbs.has(key)) return thumbs.get(key);
  thumbs.set(key, '');
  thumbQueue.push([seed, np]);
  if (!thumbBusy) nextThumb();
  return '';
}
// maps are drawn one at a time, a frame apart, so a gallery full of them never freezes the page
function nextThumb() {
  const job = thumbQueue.shift();
  if (!job) { thumbBusy = false; return; }
  thumbBusy = true;
  const [seed, np] = job, key = seed + ':' + np;
  setTimeout(() => {
    const m = genMap(seed, np), c = document.createElement('canvas'), W = 320, H = 200;
    c.width = W; c.height = H;
    paintThumb(c.getContext('2d'), m, W, H);   // the island in its own sea, with its landmarks (mapart.js)
    thumbs.set(key, c.toDataURL());
    if (G) setState(G);   // generating a map switched the world size to its own
    lobbyKey = ''; renderLobbies(); renderMaps(); if (screen === 'draft') refreshDraft();
    nextThumb();
  }, 16);
}
let lobbyKey = '';
function renderLobbies() {
  const el = $('lobbyList');
  // rebuild only when a lobby changes, never just for the clocks (tickClocks moves those): a card replaced
  // under a finger would swallow the tap
  const key = lobbies === null ? 'down' : JSON.stringify((lobbies || []).map(l => [l.code, l.title, l.np, l.humans, l.seed, thumbs.get(l.seed + ':' + l.np) ? 1 : 0]));
  if (key === lobbyKey) return;
  lobbyKey = key;
  if (lobbies === null) { el.innerHTML = '<p class="muted">Online play is not reachable right now. Solo still works.</p>'; return; }
  if (!lobbies.length) { el.innerHTML = '<p class="muted">Looking for open matches…</p>'; return; }
  el.innerHTML = lobbies.map((l, i) => {
    const img = thumb(l.seed, l.np);
    const mods = l.mods.length ? l.mods.map(id => `<i data-tip="${esc(MODS[id].name)}|${esc(MODS[id].desc)}">${esc(MODS[id].name)}</i>`).join('') : '<i data-tip="Standard rules|Nothing bent: the game as it comes.">Standard rules</i>';
    const at = `data-at="${l.startsAt}">${clock(l.startsAt - Date.now())}`;
    return `<button class="dep ${i === 0 ? 'feat' : ''}" data-a="quickJoin" data-v="${l.code}">
      <span class="pic" style="background:${seaForSeed(l.seed)}">${img ? `<img src="${img}" alt="">` : ''}<span class="clockTag" ${at}</span></span>
      <span class="lobInfo"><b>${esc(l.map || l.title)}</b><span class="modChips">${mods}</span><span class="meta">${l.np} cities · ${l.humans ? l.humans + ' aboard' : 'no passengers yet'}<span class="clockTag inline" ${at}</span></span></span>
    </button>`;
  }).join('');
}
function tickClocks() {
  for (const t of document.querySelectorAll('.clockTag')) t.textContent = clock(+t.dataset.at - Date.now());
  if (screen === 'draft' && net && net.lobby && net.lobby.public && $('leaves')) $('leaves').textContent = 'leaves in ' + clock(net.lobby.startsAt - Date.now());
}

// ---------- the map picker: every named map, filtered by how many cities it can hold ----------
const picker = { count: 4, type: 'all' };
const formWord = { real: 'Real place', island: 'Island', twin: 'Twin isles', bay: 'Bay', lagoon: 'Lagoon', coast: 'Coast', fjords: 'Fjords' };
const currentSeed = () => (net && net.kind === 'online' ? net.lobby && net.lobby.seed : net && net.seed);
const currentCount = () => (net && net.kind === 'online' ? (net.lobby ? net.lobby.np : 4) : nPlayersPref);
function openMaps() { picker.count = currentCount(); $('maps').classList.remove('hidden'); renderMaps(); }
function renderMaps() {
  if ($('maps').classList.contains('hidden')) return;
  const n = picker.count;
  $('mapCount').innerHTML = `<button data-a="mapCount" data-v="${n - 1}" ${n <= 2 ? 'disabled' : ''} aria-label="Fewer">−</button><b class="count">${n}</b><button data-a="mapCount" data-v="${n + 1}" ${n >= MAX_PLAYERS ? 'disabled' : ''} aria-label="More">+</button>` +
    [4, 8, 16, 30].map(k => `<button data-a="mapCount" data-v="${k}" class="${n === k ? 'on' : ''}">${k}</button>`).join('');
  for (const b of document.querySelectorAll('[data-a=mapType]')) b.classList.toggle('on', b.dataset.v === picker.type);
  const list = MAPS.filter(m => m.max >= n && (picker.type === 'all' || (picker.type === 'real') === (m.form === 'real')));
  const cur = currentSeed();
  $('mapGrid').innerHTML = list.length ? list.map(m => {
    const img = thumb(m.seed, n);
    return `<button class="mapCard ${m.seed === cur ? 'on' : ''}" data-a="pickMap" data-v="${m.seed}"><span class="pic" style="background:${seaForSeed(m.seed)}">${img ? `<img src="${img}" alt="">` : ''}</span><b>${esc(m.name)}</b><span class="meta">${formWord[m.form]} · up to ${m.max} cities</span></button>`;
  }).join('') : `<p class="muted">No map holds ${n} cities with this filter. Try fewer cities, or show all.</p>`;
}
// choosing a map sets the city count from the picker too, since that is what the player filtered by
function pickMap(seed) {
  const n = picker.count, s = seed || (MAPS.filter(m => m.max >= n)[Math.floor(Math.random() * MAPS.filter(m => m.max >= n).length)] || {}).seed || rnd();
  $('maps').classList.add('hidden');
  if (net.kind === 'online') net.host('settings', { players: n, seed: s });
  else { nPlayersPref = n; newSolo(s); }
}

// ---------- failure: say so instead of freezing ----------
function fatal(err) {
  if (failed) return;
  console.error(err);
  // the message on screen makes a screenshot enough to find the bug; online, the server log gets it too
  const msg = String(err && err.stack ? err.stack.split('\n').slice(0, 3).join(' ') : err).slice(0, 400);
  $('fatalDetail').textContent = `v${VERSION}: ${msg}`;
  if (net && net.kind === 'online') net.raw({ t: 'clientError', v: VERSION, msg });
  $('fatalText').textContent = 'The game hit an error and stopped. Reloading usually fixes it' + (net && net.kind === 'online' ? ', and you will rejoin your room.' : '.');
  $('fatal').classList.remove('hidden');
  failed = true;
}
let failed = false;
addEventListener('error', e => { if (e.error) fatal(e.error); });
addEventListener('unhandledrejection', e => fatal(e.reason));

// ---------- frame loop ----------
let last = performance.now(), hudT = 0, lastMap = null, lastPhase = null;
// one frame of everything but drawing: the game, events, the tutorial, the HUD
function step(dt, raw = dt) {
  if (net) {
    net.tick(dt);
    setState(net.view);
    if (G) for (const ev of net.drain()) handleEvent(ev);
  }
  if (G && (G.map !== lastMap || G.phase !== lastPhase)) {
    // a new match (or a new map without leaving play): its song takes over. Annexing redraws the map, so go by name.
    if (G.phase === 'play' && (lastPhase !== 'play' || G.map.name !== lastMapName)) { lastMapName = G.map.name; Music.scene('match', G.map); }
    lastMap = G.map; lastPhase = G.phase; resize();
  }
  musicTick();   // the setup card changes the space the island fits in
  if (screen === 'draft' && G && G.phase === 'play') setScreen('play');
  if (tutorialOn()) tutorialTick();
  for (let i = UI.fx.length - 1; i >= 0; i--) { UI.fx[i].t += dt; if (UI.fx[i].t > 2) UI.fx.splice(i, 1); }
  for (let i = UI.pops.length - 1; i >= 0; i--) { UI.pops[i].t += dt; if (UI.pops[i].t > 0.9) UI.pops.splice(i, 1); }
  hudT -= raw; if (hudT <= 0) { hudT = 0.15; hud(); if (screen === 'draft') refreshDraft(); tickClocks(); }
}
// the music follows the match: rush hour, and how close your busiest platform is to overflowing, make it busier,
// and each of your trains pulling into a station is a note for Signal Box
const trainAt = new Map();
function musicTick() {
  const p = G && G.phase === 'play' && (screen === 'play' || screen === 'pause') ? me() : null;
  if (!p || !p.alive) { Music.setIntensity(0.12); trainAt.clear(); return; }
  Music.setIntensity(Math.max(0.1, (rushHour() - 1) / 4, maxCrowd(p) * 0.8));
  p.lines.forEach((L, li) => L.trains.forEach((T, ti) => {
    const key = L.id + ':' + ti, was = trainAt.get(key);
    if (T.to == null && was !== T.idx) { if (was !== undefined) Music.arrive(li, T.idx); trainAt.set(key, T.idx); }
  }));
}
function frame(now) {
  if (failed) return;
  const raw = (now - last) / 1000, dt = Math.min(0.05, raw); last = now;
  try { step(dt, raw); render(); } catch (e) { fatal(e); return; }
  requestAnimationFrame(frame);
}

// ---------- boot ----------
try { UI.colourBlind = localStorage.getItem('trammed-cb') === '1'; } catch (e) { /* storage blocked */ }
$('ver').textContent = VERSION; $('topVer').textContent = 'v' + VERSION;
try { $('nameIn').value = localStorage.getItem('trammed-name') || ''; } catch { /* storage blocked */ }
$('vol').addEventListener('input', e => Music.setVolume(e.target.value / 100));
// the Song picker, on the front page and in the menu: Map music (the default) plays each map's own song
const scoreOpts = `<option value="map">Map music</option><option value="shuffle">Shuffle</option>` + SCORES.map(sc => `<option value="${sc.id}">${sc.name}</option>`).join('');
for (const id of ['scoreSel', 'hubScore']) {
  $(id).innerHTML = scoreOpts; $(id).value = Music.score;
  $(id).addEventListener('change', e => { Music.start(); Music.setScore(e.target.value); refreshMenu(); });
}
for (const ev of ['pointerdown', 'keydown']) addEventListener(ev, () => Music.start(), { once: true });
readTheme();
matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => { readTheme(); resetHud(); });
addEventListener('resize', resize);
startDemo();
// an invite link opens the join form; a reload mid-match rejoins the same seat
const invite = params.get('room'), saved = (() => { try { return JSON.parse(sessionStorage.getItem('trammed-seat') || 'null'); } catch { return null; } })();
if (saved && (!invite || saved.code === invite.toUpperCase())) { ensureOnline(); net.rejoinSaved(); }
else if (invite) { $('codeIn').value = invite.toUpperCase().slice(0, 4); $('onlineErr').textContent = 'Invited to room ' + $('codeIn').value + ': press Join lobby.'; }
requestAnimationFrame(frame);
// dev only: step(secs) runs whole frames by hand, for testing in a background tab where animation frames stop
if (DEV) window.__trammed = { get G() { return G; }, get net() { return net; }, UI, step: secs => { for (let t = 0; t < secs; t += 0.05) step(0.05); render(); } };
